import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { Heart, MessageCircle, X } from "lucide-react";
import { tx } from "../lib/tx";

export default function MatchModal({ isOpen, onClose, matchedUser, currentUser, onMatchCelebrated }) {
  const navigate = useNavigate();

  if (!isOpen || !matchedUser) return null;

  const handleSendMessage = () => {
    // Trigger review prompt after match celebration
    if (onMatchCelebrated) {
      onMatchCelebrated();
    }
    onClose();
    navigate(`/chat/${matchedUser.user_id}`);
  };

  const handleContinueSwiping = () => {
    // Trigger review prompt after match celebration
    if (onMatchCelebrated) {
      onMatchCelebrated();
    }
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          data-testid="match-modal"
        >
          {/* Backdrop */}
          <motion.div 
            className="absolute inset-0 bg-black/90 backdrop-blur-sm"
            onClick={onClose}
          />
          
          {/* Content */}
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.5, opacity: 0 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
            className="relative z-10 text-center max-w-sm w-full"
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-0 right-0 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-all"
              data-testid="close-match-modal"
            >
              <X className="w-5 h-5" />
            </button>
            
            {/* Hearts animation */}
            <motion.div
              animate={{ 
                scale: [1, 1.2, 1],
              }}
              transition={{ 
                duration: 1,
                repeat: Infinity,
                repeatType: "reverse"
              }}
              className="flex justify-center mb-4"
            >
              <Heart className="w-16 h-16 text-primary fill-primary" />
            </motion.div>
            
            {/* Title */}
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-gradient mb-6">
              {tx("C'est un Match !")}
            </h2>
            
            {/* Avatars */}
            <div className="flex justify-center items-center gap-4 mb-8">
              <div className="relative">
                <img 
                  src={currentUser?.picture || currentUser?.photos?.[0] || "https://ui-avatars.com/api/?name=You"} 
                  alt="You"
                  className="w-24 h-24 rounded-full object-cover border-4 border-primary"
                />
              </div>
              
              <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center">
                <Heart className="w-6 h-6 text-white fill-white" />
              </div>
              
              <div className="relative">
                <img 
                  src={matchedUser?.picture || matchedUser?.photos?.[0]} 
                  alt={matchedUser?.name}
                  className="w-24 h-24 rounded-full object-cover border-4 border-primary"
                />
              </div>
            </div>
            
            <p className="text-white/80 mb-8">
              {tx("Vous et")} <span className="font-semibold text-white">{matchedUser?.name}</span> {tx("vous êtes mutuellement likés !")}
            </p>
            
            {/* Actions */}
            <div className="flex flex-col gap-3">
              <Button
                onClick={handleSendMessage}
                className="w-full h-14 rounded-full bg-primary text-white font-bold text-lg hover:shadow-[0_0_20px_rgba(225,29,72,0.5)] transition-all"
                data-testid="send-message-btn"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                {tx("Envoyer un message")}
              </Button>
              
              <Button
                onClick={handleContinueSwiping}
                variant="ghost"
                className="w-full h-14 rounded-full text-white/70 hover:text-white hover:bg-white/10"
                data-testid="continue-swiping-btn"
              >
                {tx("Continuer à swiper")}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
